import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from '@tanstack/react-router';
import { SendHorizonal } from 'lucide-react';
import {
  Button,
  UserCard,
  getUserFullName,
  createColumnHeader,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { DueDateCell } from '@/lib/due-date-utils';
import { useAuthStore } from '@/stores/auth-store';
import { useStandardRecordingTimeMs } from '@/hooks/useStandardRecordingTime';
import type { NotStartedProcess } from '@bliks-io/contracts';
import { toUserCardProps, type NudgeTarget } from '../types';

interface UseNotStartedColumnsOptions {
  onNudge: (target: NudgeTarget) => void;
}

export function useNotStartedColumns({ onNudge }: UseNotStartedColumnsOptions) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slug = useAuthStore().slug();
  const standardRecordingTimeMs = useStandardRecordingTimeMs();

  return useMemo(
    (): DataTableColumnDef<NotStartedProcess>[] => [
      {
        accessorKey: 'name',
        header: createColumnHeader(t('recordingDashboard.columns.process')),
        cell: ({ row }) => (
          <Link
            to="/$slug/processes/all-processes/$processId"
            params={{ slug: slug!, processId: row.original._id }}
            className="text-sm font-medium hover:underline"
          >
            {row.original.name}
          </Link>
        ),
        enableSorting: true
      },
      {
        accessorKey: 'owner',
        header: createColumnHeader(t('recordingDashboard.columns.owner')),
        cell: ({ row }) => (
          <UserCard user={toUserCardProps(row.original.owner)} variant="with-role" size="sm" />
        ),
        accessorFn: (row) => `${row.owner.firstName} ${row.owner.lastName}`,
        enableSorting: true
      },
      {
        id: 'dueDate',
        header: createColumnHeader(t('recordingDashboard.columns.dueDate')),
        accessorFn: (row) => row.dueDate,
        cell: ({ row }) => (
          <DueDateCell
            dueDate={row.original.dueDate}
            standardRecordingTimeMs={standardRecordingTimeMs}
          />
        ),
        enableSorting: true
      },
      {
        id: 'actions',
        meta: { sticky: 'right' as const, fitContent: true },
        header: () => <div className="sr-only">{t('recordingDashboard.columns.actions')}</div>,
        cell: ({ row }) => {
          const process = row.original;

          return (
            <div className="flex gap-1">
              {process.canNudge && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() =>
                    onNudge({
                      userId: process.owner._id,
                      name: getUserFullName(process.owner.firstName, process.owner.lastName),
                      processId: process._id,
                      processName: process.name
                    })
                  }
                >
                  <SendHorizonal className="h-4 w-4" />
                </Button>
              )}
              {process.canStart && (
                <Button
                  variant="outline"
                  size="small"
                  onClick={() =>
                    navigate({
                      to: '/$slug/processes/recording/start-recording/$processId',
                      params: {
                        slug: slug!,
                        processId: process._id
                      }
                    })
                  }
                >
                  {t('recordingDashboard.notStarted.startRecording')}
                </Button>
              )}
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      }
    ],
    [t, slug, navigate, onNudge, standardRecordingTimeMs]
  );
}
